/**
 * HubSpot native-form loaders. Portal/form IDs live in constants.ts; this file
 * only knows how to load the two official embed scripts once per page.
 */

import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from '@/lib/constants';
import type { HubSpotEmbed } from '@/lib/types';

interface HubSpotV2CreateOptions {
  region: string;
  portalId: string;
  formId: string;
  target: string;
  onFormReady?: () => void;
  onFormSubmitted?: () => void;
}

/** Subset of the v4 form instance used for optional subject prefill. */
export interface HubSpotV4Form {
  getFormId: () => string;
  getInstanceId: () => string;
  getFormFieldValues: () => Promise<Array<{ name: string }>>;
  getFieldValue: (name: string) => Promise<unknown>;
  setFieldValue: (name: string, value: string) => void;
}

export interface HubSpotV4EventDetail {
  formId?: string;
  instanceId?: string;
}

declare global {
  interface Window {
    hbspt?: { forms?: { create?: (options: HubSpotV2CreateOptions) => void } };
    HubSpotFormsV4?: { getFormFromEvent?: (event: Event) => HubSpotV4Form | null };
  }
}

const SCRIPT_TIMEOUT_MS = 15000;

export const HUBSPOT_EMBED_URLS: Record<HubSpotEmbed, string> = {
  v2: 'https://js.hsforms.net/forms/embed/v2.js',
  v4: `https://js-${HUBSPOT_REGION}.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`,
};

/** Marks the tags this module owns: `loading` | `loaded` | `failed`. */
const STATE_ATTR = 'data-cid-hubspot';

const pending: Partial<Record<HubSpotEmbed, Promise<void>>> = {};

function isReady(embed: HubSpotEmbed, script: HTMLScriptElement | null): boolean {
  if (embed === 'v2') return typeof window.hbspt?.forms?.create === 'function';
  // The v4 loader observes new `.hs-form-frame` mounts itself once it has run.
  return script?.getAttribute(STATE_ATTR) === 'loaded';
}

/**
 * Load an embed script once per page, however many forms ask concurrently
 * (single-flight). A slow load rejects after SCRIPT_TIMEOUT_MS but the tag is
 * kept, so a retry waits on the same request instead of injecting a second
 * copy (a removed tag that already started fetching would still execute).
 * Only a tag that errored is replaced.
 */
export function loadHubSpotEmbed(embed: HubSpotEmbed): Promise<void> {
  const url = HUBSPOT_EMBED_URLS[embed];
  let script = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
  if (isReady(embed, script)) return Promise.resolve();
  const inFlight = pending[embed];
  if (inFlight) return inFlight;

  if (script && script.getAttribute(STATE_ATTR) !== 'loading') {
    // Failed, or loaded without initializing: start over with a fresh tag.
    script.remove();
    script = null;
  }
  if (!script) {
    script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    script.setAttribute(STATE_ATTR, 'loading');
    // Permanent state tracking (registered first): survives a timed-out wait,
    // so a retry after a late load sees `loaded` instead of waiting forever.
    const created = script;
    created.addEventListener('load', () => created.setAttribute(STATE_ATTR, 'loaded'), {
      once: true,
    });
    created.addEventListener('error', () => created.setAttribute(STATE_ATTR, 'failed'), {
      once: true,
    });
    document.head.appendChild(script);
  }
  const tag = script;

  const promise = new Promise<void>((resolve, reject) => {
    const settle = (error?: string) => {
      window.clearTimeout(timer);
      tag.removeEventListener('load', onLoad);
      tag.removeEventListener('error', onError);
      if (error) reject(new Error(error));
      else resolve();
    };
    const onLoad = () =>
      settle(isReady(embed, tag) ? undefined : 'HubSpot form service did not initialize.');
    const onError = () => settle('HubSpot form service could not load.');
    const timer = window.setTimeout(
      () => settle('HubSpot form service timed out.'),
      SCRIPT_TIMEOUT_MS
    );
    tag.addEventListener('load', onLoad);
    tag.addEventListener('error', onError);
  });

  pending[embed] = promise;
  // Settled either way: later calls re-check readiness (or retry) from scratch.
  const clear = () => {
    if (pending[embed] === promise) delete pending[embed];
  };
  promise.then(clear, clear);
  return promise;
}

export const HUBSPOT_V4_EVENTS = {
  ready: 'hs-form-event:on-ready',
  success: 'hs-form-event:on-submission:success',
  failed: 'hs-form-event:on-submission:failed',
} as const;

/** Ticket "Subject" property on the Help Desk form. */
export const HUBSPOT_SUBJECT_FIELD = '0-5/subject';
